import { supabase } from '@/integrations/supabase/client';
import type { BrandProfile, PersonalWritingStyle, WritingIdentity } from '@/types';

export type MigrationSourceType = 'personal_writing_style' | 'brand_profile';

function normalizeTextArray(values: string[] | null | undefined): string[] {
  if (!Array.isArray(values)) return [];
  return Array.from(
    new Set(
      values
        .map((value) => value?.trim())
        .filter((value): value is string => Boolean(value))
    )
  );
}

export async function getWritingIdentity(id: string): Promise<WritingIdentity | null> {
  const { data, error } = await supabase
    .from('writing_identities')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) throw error;
  return (data as WritingIdentity | null) ?? null;
}

export async function getWritingIdentityByMigrationSource(
  migratedFromType: MigrationSourceType,
  migratedFromId: string,
): Promise<WritingIdentity | null> {
  const { data, error } = await supabase
    .from('writing_identities')
    .select('*')
    .eq('migrated_from_type', migratedFromType)
    .eq('migrated_from_id', migratedFromId)
    .order('updated_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return (data as WritingIdentity | null) ?? null;
}

export async function resolveWritingIdentityBySource(
  sourceType: MigrationSourceType,
  sourceId: string,
): Promise<WritingIdentity | null> {
  const migrated = await getWritingIdentityByMigrationSource(sourceType, sourceId);
  if (migrated) return migrated;

  // v2-first fallback: if the source ID itself is now used as writing identity ID.
  return getWritingIdentity(sourceId);
}

export async function upsertWritingIdentityFromPersonalStyle(style: PersonalWritingStyle): Promise<WritingIdentity | null> {
  const existing = await resolveWritingIdentityBySource('personal_writing_style', style.id);

  const payload = {
    identity_type: 'personal',
    name: style.name,
    description: style.description ?? null,
    formality: style.formality,
    playfulness: style.playfulness,
    warmth: style.warmth,
    complexity: style.complexity,
    optimism: style.optimism,
    personalization: style.personalization,
    primary_archetype: style.primary_archetype || 'everyman',
    secondary_archetype: style.secondary_archetype ?? null,
    writing_personality: style.writing_personality ?? style.description ?? null,
    word_patterns: style.word_patterns ?? null,
    stance_profile: style.stance_profile ?? null,
    variance_tolerance: style.variance_tolerance ?? null,
    lexical_bias: style.lexical_bias_profile ?? null,
    editorial_philosophy: style.editorial_philosophy ?? null,
    stylometric_fingerprint: {
      avg_sentence_length: style.avg_sentence_length,
      sentence_complexity: style.sentence_complexity,
      vocabulary_richness: style.vocabulary_richness,
      lexical_rarity: style.lexical_rarity,
      punctuation_density: style.punctuation_density,
      adjective_adverb_ratio: style.adjective_adverb_ratio,
      repetition_index: style.repetition_index,
      pronoun_usage: style.pronoun_usage,
    },
    confidence: style.confidence ?? null,
    analysis_reasoning: style.analysis_reasoning ?? null,
    default_purpose: style.default_purpose ?? null,
    is_active: Boolean(style.is_active),
    migrated_from_id: style.id,
    migrated_from_type: 'personal_writing_style',
    schema_version: 2,
  };

  if (existing?.id) {
    const { data, error } = await supabase
      .from('writing_identities')
      .update(payload)
      .eq('id', existing.id)
      .select('*')
      .single();

    if (error) {
      console.error('[writingIdentities] Failed updating personal mirror:', error);
      return null;
    }

    return data as WritingIdentity;
  }

  const { data, error } = await supabase
    .from('writing_identities')
    .insert({
      ...payload,
      user_id: style.user_id,
    })
    .select('*')
    .single();

  if (error) {
    console.error('[writingIdentities] Failed creating personal mirror:', error);
    return null;
  }

  return data as WritingIdentity;
}

export async function upsertWritingIdentityFromBrandProfile(profile: BrandProfile): Promise<WritingIdentity | null> {
  const existing = await resolveWritingIdentityBySource('brand_profile', profile.id);

  const preferredTerminology = normalizeTextArray(profile.word_list);
  const disallowedLanguage = normalizeTextArray(profile.banned_terms);

  const payload = {
    identity_type: 'brand',
    name: profile.name,
    description: profile.additional_notes ?? null,
    formality: profile.formality,
    playfulness: profile.playfulness,
    warmth: profile.warmth,
    complexity: profile.complexity,
    optimism: profile.optimism,
    personalization: profile.personalization,
    primary_archetype: profile.primary_archetype || 'everyman',
    secondary_archetype: profile.secondary_archetype ?? null,
    product_description: profile.product_description ?? null,
    value_description: profile.value_description ?? null,
    target_audience: profile.target_audience ?? null,
    additional_notes: profile.additional_notes ?? null,
    content_interests: normalizeTextArray(profile.content_interests),
    preferred_terminology: preferredTerminology,
    disallowed_language: disallowedLanguage,
    default_purpose: profile.default_purpose ?? null,
    is_active: Boolean(profile.is_active),
    migrated_from_id: profile.id,
    migrated_from_type: 'brand_profile',
    schema_version: 2,
  };

  if (existing?.id) {
    const { data, error } = await supabase
      .from('writing_identities')
      .update(payload)
      .eq('id', existing.id)
      .select('*')
      .single();

    if (error) {
      console.error('[writingIdentities] Failed updating brand mirror:', error);
      return null;
    }

    return data as WritingIdentity;
  }

  const { data, error } = await supabase
    .from('writing_identities')
    .insert({
      ...payload,
      user_id: profile.user_id,
    })
    .select('*')
    .single();

  if (error) {
    console.error('[writingIdentities] Failed creating brand mirror:', error);
    return null;
  }

  return data as WritingIdentity;
}

export async function getBrandDisallowedLanguage(brandProfileId: string): Promise<string[]> {
  const identity = await resolveWritingIdentityBySource('brand_profile', brandProfileId);
  if (identity?.identity_type === 'brand' && Array.isArray(identity.disallowed_language)) {
    return normalizeTextArray(identity.disallowed_language);
  }

  const { data, error } = await supabase
    .from('brand_profiles')
    .select('banned_terms')
    .eq('id', brandProfileId)
    .maybeSingle();

  if (error) throw error;
  return normalizeTextArray(data?.banned_terms ?? []);
}
